import { Mail, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { lazy, Suspense, useEffect, useState } from 'react';
import { BookingProvider } from '../../context/BookingContext';

const BookingModal = lazy(() => import('../booking/BookingModal'));

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className="h-5 w-5">
    <path d="M13.5 22v-8.2h2.8l.4-3.2h-3.2V8.6c0-.9.2-1.6 1.6-1.6H17V4.1c-.4 0-1.7-.2-3.1-.2-3.2 0-5.4 2-5.4 5.7v1.4H5.1v3.2h3.4V22h5z" />
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className="h-5 w-5">
    <path d="M21.8 8.1s-.2-1.4-.8-2c-.8-.9-1.6-.9-2-1-2.9-.2-7-.2-7-.2s-4.1 0-7 .2c-.4 0-1.2.1-2 1-.6.6-.8 2-.8 2S2 9.7 2 11.4v1.2c0 1.7.2 3.3.2 3.3s.2 1.4.8 2c.8.9 1.8.8 2.3 1 1.7.1 6.7.2 6.7.2s4.1 0 7-.2c.4 0 1.2-.1 2-1 .6-.6.8-2 .8-2s.2-1.6.2-3.3v-1.2c0-1.7-.2-3.3-.2-3.3Zm-11.3 6.6V9.4l5.1 2.7-5.1 2.6Z" />
  </svg>
);

const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className="h-5 w-5">
    <path d="M6.94 6.5A1.94 1.94 0 1 1 3.06 6.5a1.94 1.94 0 0 1 3.88 0ZM3.4 8.75h3.1V21h-3.1V8.75ZM9.2 8.75h2.98v1.67h.04c.42-.8 1.45-1.64 2.98-1.64 3.19 0 3.78 2.1 3.78 4.83V21h-3.1v-5.35c0-1.28-.02-2.93-1.78-2.93-1.79 0-2.06 1.4-2.06 2.83V21H9.2V8.75Z" />
  </svg>
);

const Footer = () => {
  const basePath = import.meta.env.VITE_BASE_PATH || '';
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;

      const hasDirectGatewayMarker = Boolean(
        params.get('MerchantID') ||
        params.get('merchantRID') ||
        params.get('merchantId') ||
        params.get('PaymentGatewayRef') ||
        params.get('paymentGatewayRef') ||
        params.get('trId') ||
        params.get('TrId') ||
        params.get('ExtRefId') ||
        params.get('extRefId') ||
        params.get('txnid')
      );

      if (hasDirectGatewayMarker) {
        const merchantId = params.get('merchantId') || params.get('MerchantID') || params.get('merchantRID');
        let paymentGatewayRef = params.get('paymentGatewayRef') || params.get('PaymentGatewayRef');
        const extRefId = params.get('extRefId') || params.get('ExtRefId');
        let trId = params.get('trId') || params.get('TrId');
        const billId = params.get('billId') || params.get('BillId');

        if (!trId) {
          const txnIdRaw = params.get('txnid') || '';
          const trMatch = String(txnIdRaw).match(/[?&]trId=([^&]+)/i);
          if (trMatch && trMatch[1]) {
            trId = decodeURIComponent(trMatch[1]);
          }
        }
        if (!paymentGatewayRef && trId) {
          paymentGatewayRef = trId;
        }

        if (merchantId) window.localStorage.setItem('medica_pg_merchantId', merchantId);
        if (paymentGatewayRef) window.localStorage.setItem('medica_pg_paymentGatewayRef', paymentGatewayRef);
        if (extRefId) window.localStorage.setItem('medica_pg_extRefId', extRefId);
        if (trId) window.localStorage.setItem('medica_pg_trId', trId);
        if (billId) window.localStorage.setItem('medica_pg_billId', billId);
        window.localStorage.setItem('medica_pg_returnedAt', String(Date.now()));
        window.localStorage.setItem('medica_pg_returnQuery', url.search || '');

        params.delete('merchantId');
        params.delete('MerchantID');
        params.delete('merchantRID');
        params.delete('merchantRid');
        params.delete('paymentGatewayRef');
        params.delete('PaymentGatewayRef');
        params.delete('extRefId');
        params.delete('ExtRefId');
        params.delete('trId');
        params.delete('TrId');
        params.delete('txnid');
        params.delete('billId');
        params.delete('BillId');
        params.delete('status');
        params.delete('Status');

        const nextUrl = `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash || ''}`;
        window.history.replaceState({}, document.title, nextUrl);
      }

      const returnedAt = window.localStorage.getItem('medica_pg_returnedAt');
      const resumeRaw = window.localStorage.getItem('medica_pg_resume_v1');
      const consumed = window.localStorage.getItem('medica_pg_resume_consumed');
      if (!returnedAt || !resumeRaw || consumed === '1') return;

      const resume = JSON.parse(resumeRaw);
      const expiresAt = Number(resume?.expiresAt || 0);
      if (!expiresAt || Date.now() > expiresAt) return;

      window.localStorage.setItem('medica_pg_resume_consumed', '1');
      setIsModalOpen(true);
    } catch (e) {
      // ignore
    }
  }, []);

  const handleBookDoctor = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  return (
    <footer
      style={{ background: 'linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(243, 237, 237, 1))' }}
      className="md:mt-20"
    >
      <div className="container px-4 py-12 mx-auto">

        {/* Mobile Footer Layout */}
        <div className="space-y-6 lg:hidden">
          {/* Logo */}
          <div>
            <img
              src={`${basePath}/logo/medica-logo.png`}
              alt="Medica Logo"
              width={200}
              height={80}
              className="object-contain"
            />
          </div>

          {/* Description */}
          <p className="text-xs leading-relaxed text-center text-gray-700">
            {t('footer_description')}
          </p>

          <hr className="border-gray-300" />

          {/* mobiOs Logo + social icons */}
          <div className="flex flex-col items-center">
            <img
              src={`${basePath}/logo/mobios-logo.png`}
              alt="mobiOs Logo"
              width={120}
              height={40}
              className="object-contain"
            />
            <div className="flex gap-4 mt-3">
              <a href="https://www.facebook.com/profile.php?id=61562093174467" aria-label="Facebook" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer">
                <FacebookIcon />
              </a>
              <a href="https://www.youtube.com/@medica3542" aria-label="YouTube" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer">
                <YouTubeIcon />
              </a>
              <a href="https://www.linkedin.com/showcase/medica-cms/" aria-label="LinkedIn" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer">
                <LinkedinIcon />
              </a>
              <a href="mailto:info@medica.lk" aria-label="Mail" className="text-gray-400 hover:text-gray-600">
                <Mail size={20} />
              </a>
            </div>
          </div>

          {/* Disclaimer */}
          {/* <p className="text-xs leading-relaxed text-gray-600">
            {t('footer_disclaimer')}
          </p> */}

          {/* (mobile) social icons moved above with mobios logo to avoid duplication */}

          
          

          {/* Terms Links - Single Line with Separators */}
          <div className="text-xs text-gray-600">
            <Link to="/terms" className="hover:text-gray-800">{t('footer_terms')}</Link>
            <span className="mx-2">|</span>
            <Link to="/privacy-policy" className="hover:text-gray-800">{t('footer_privacy')}</Link>
            <span className="mx-2">|</span>
            <Link to="/cookie-policy" className="hover:text-gray-800">{t('footer_cookie_policy')}</Link>
          </div>

          {/* Copyright */}
          <p className="text-xs text-gray-700">{t('footer_copyright', { year: new Date().getFullYear() })}</p>
        </div>

        {/* Desktop Footer Layout */}
        <div className="hidden lg:block">
          {/* Top section */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-6">
            {/* Logo */}
            <div className="lg:col-span-1">
              <img
                src={`${basePath}/logo/medica-logo.png`}
                alt="Medica Logo"
                width={160}
                height={60}
                className="object-contain"
              />
            </div>

            {/* MEDICA Links */}
            <div>
              <h3 className="mb-4 font-semibold text-gray-800">{t('footer_medica')}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/" className="hover:text-gray-800">{t('footer_home')}</Link></li>
                <li><a href="#" onClick={handleBookDoctor} className="cursor-pointer hover:text-gray-800">{t('footer_book_doctor')}</a></li>
                <li><Link to="/company/about-us" className="hover:text-gray-800">{t('footer_about_us')}</Link></li>
                <li><Link to="/contact-us" className="hover:text-gray-800">{t('footer_contact')}</Link></li>
              </ul>
            </div>

            {/* OUR PRODUCTS Links */}
            <div>
              <h3 className="mb-4 font-semibold text-gray-800">{t('footer_our_products')}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/products/medica-clinic" className="hover:text-gray-800">{t('footer_medica_clinic')}</Link></li>
                <li><Link to="/products/medica-reception" className="hover:text-gray-800">{t('footer_medica_reception')}</Link></li>
                <li><Link to="/products/medica-patient-app" className="hover:text-gray-800">{t('footer_medica_patient_app')}</Link></li>
                <li><Link to="/products/medica-pharmacy" className="hover:text-gray-800">{t('footer_medica_pharmacy')}</Link></li>
                <li><Link to="/products/medica-telemedicine" className="hover:text-gray-800">{t('footer_medica_telemedicine')}</Link></li>
              </ul>
            </div>

            {/* LEGAL Links */}
            <div>
              <h3 className="mb-4 font-semibold text-gray-800">{t('footer_legal')}</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/terms" className="hover:text-gray-800">{t('footer_terms')}</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-gray-800">{t('footer_privacy')}</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-gray-800">{t('footer_cookie_policy')}</Link></li>
              </ul>
            </div>

            {/* CONTACT US */}
            <div>
              <h3 className="mb-4 font-semibold text-gray-800">{t('footer_contact_us')}</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <Phone size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="mb-1 text-xs">{t('footer_call_us_directly')}</p>
                    <a href="tel:+94713304304" className="font-medium hover:text-gray-800">+94713304304</a>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Mail size={16} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="mb-1 text-xs">{t('footer_email_us_on')}</p>
                    <a href="mailto:info@medica.lk" className="font-medium hover:text-gray-800">info@medica.lk</a>
                  </div>
                </div>
              </div>
            </div>

            {/* ADDRESS */}
            <div>
              <h4 className="mb-4 font-semibold text-gray-800">{t('footer_address')}</h4>
              <div className="flex items-start gap-2 text-sm text-gray-600">
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                  <p>{t('footer_address_line_1')}</p>
                  <p>{t('footer_address_line_2')}</p>
                  <p>{t('footer_address_line_3')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Middle section */}
          <div className="flex justify-center mt-4">
            <p className="max-w-4xl text-sm text-center text-gray-500">{t('footer_description')}</p>
          </div>

          <hr className="my-4 mt-2 border-2 border-gray-300" />

          {/* Bottom Section */}
          <div className="flex flex-col items-center justify-between text-sm text-gray-600 md:flex-row">
            <div>
              {/* <p className="text-xs text-gray-500">{t('footer_disclaimer')}</p> */}
              <p className="mt-2">{t('footer_copyright', { year: new Date().getFullYear() })}</p>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="flex flex-col items-center">
                <img
                  src={`${basePath}/logo/mobios-logo.png`}
                  alt="mobiOs Logo"
                  width={120}
                  height={40}
                  className="object-contain"
                />
                <div className="flex gap-4 mt-3">
                  <a href="https://www.facebook.com/profile.php?id=61562093174467" aria-label="Facebook" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer"><FacebookIcon /></a>
                  <a href="https://www.youtube.com/@medica3542" aria-label="YouTube" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer"><YouTubeIcon /></a>
                  <a href="https://www.linkedin.com/showcase/medica-cms/" aria-label="LinkedIn" className="text-gray-400 hover:text-gray-600" target="_blank" rel="noopener noreferrer"><LinkedinIcon /></a>
                  <a href="mailto:info@medica.lk" aria-label="Mail" className="text-gray-400 hover:text-gray-600"><Mail size={20} /></a>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Booking Modal */}
      <BookingProvider>
        <Suspense fallback={null}>
          {isModalOpen ? (
            <BookingModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
            />
          ) : null}
        </Suspense>
      </BookingProvider>
    </footer>
  );
};

export default Footer;
